import {FormState, FormStateWithIssues} from '@raison/form-models';
import {useSearchParamStructured} from '@raison/navigation';
import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import React, {useEffect, useReducer} from 'react';
import {Navigate, useNavigate} from 'react-router';
import {MAX_DECIMALS_IN_CRYPTO} from '~/config';
import {useConversionRate, useCurrencyFormat} from '~/entities/currencies';
import {GenericErrorMsg} from '~/entities/dialogs';
import {FallbackPage, NotFoundPage} from '~/entities/fallbacks';
import {AccountId, OperationDetail, OperationSelectButton} from '~/entities/operations-fiat';
import {executeOperationFinishNavigation} from '~/entities/transactions-history/model/TransactionDetailsPath';
import {RootLayout, RsnAmount, RsnAmountInput, RsnButton, RsnDialog, RsnSkeleton, useBoolean} from '~/library/ui-kit';
import {OrderPlacedInfoPage} from '~/processes/crypto-send-and-receive/ui/TxPlacedPage';
import {assetsRequestRoutine} from '~/redux/assets/assetsActions';
import {useAppDispatch} from '~/shared/store';
import {useSubmitBuyTxRequest} from '../api/submitBuyTx';
import {BlockchainAccount, BuyTxResource, FiatAccount, OrderId, useBuyTxResource} from '../model/BuyTxResource';

const parseProps = type({
    from: ['string.numeric.parse', '=>', AccountId],
    to: ['string.numeric.parse', '=>', AccountId],
});

export function BuyContainer(rawProps: {from: string; to: string}) {
    const {from, to} = parseProps.from({...rawProps});

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(assetsRequestRoutine.trigger());
    }, []);

    const resource = useBuyTxResource(from, to);

    if (resource.isError) {
        return <FallbackPage />;
    }

    if (resource.isLoading || resource.isUninitialized) {
        return <LoadingPage />;
    }

    if (!resource.data) {
        return <NotFoundPage />;
    }

    return <BuyPage resource={resource.data} />;
}

type BuyFormState = ReturnType<typeof BuyFormState>;
const BuyFormState = () =>
    FormState.from({
        amount: RsnAmount.empty(),
    });

const parsParam = type.never
    .or({
        view: "'form'",
        dialog: "'not-enough-funds'?",
    })
    .or({
        view: "'finish'",
        qty: 'string',
        orderId: OrderId,
    });
const decodeParam = parsParam.assert.bind(parsParam);

type Values = FormState.inferValues<BuyFormState>;

function BuyPage({resource: {accountFrom, accountTo, minimum, absoluteFee, relativeFee}}: {resource: BuyTxResource}) {
    const [direction, setDirection] = useSearchParamStructured('BuyTx', {
        decode: decodeParam,
        fallback: {view: 'form'},
    });
    const [isFormCompleted, formCompleted] = useBoolean(false);

    const {format: formatAsSource} = useCurrencyFormat(accountFrom);
    const {format: formatAsTarget} = useCurrencyFormat(accountTo);
    const {format: formatAsApprox} = useCurrencyFormat(accountTo, {approx: true});

    const t = {
        Title: `Buy ${accountTo.code}`,
        MinHint: `Min: ${formatAsSource(minimum)}`,
        NotEnoughFunds: 'Not enough funds',
        UseAllFunds: 'Use all funds',
        BalanceFrom: `Balance: ${formatAsSource(accountFrom.balance)}`,
        BalanceTo: `Balance: ${formatAsTarget(accountTo.balance)}`,
        ApproxValue: 'Approx value',
        Fee: 'Fee',
        TotalReceive: 'Total receive',
        Buy: 'Buy',
    };

    const [_form, dispatch] = useReducer(FormState.reduce<Values>, null, BuyFormState);

    const values = FormState.toValues(_form);

    const formWithIssues = FormStateWithIssues.fromRules(_form, {
        amount: [
            RsnAmount.validateMin(minimum, t.MinHint),
            RsnAmount.validateMax(accountFrom.balance, t.NotEnoughFunds),
        ],
    });

    const handleError = useErrorHandler();
    const {mutateAsync} = useSubmitBuyTxRequest(accountTo.id, accountTo.type);

    const {fields, isSubmitDisabled, onSubmit} = FormState.selectFormView(formWithIssues, {
        dispatch,
        async submit() {
            const {problem: issues, value: orderId} = await mutateAsync({
                account: accountFrom.id,
                amount: RsnAmount.toNumber(values.amount),
            }).catch(handleError);

            if (issues?.amount.startsWith('Not enough balance')) {
                setDirection(dir => ({...dir, dialog: 'not-enough-funds'}));
                return true;
            } else if (issues) {
                return issues;
            } else {
                formCompleted.on();
                setDirection({view: 'finish', qty: RsnAmount.toString(values.amount), orderId});
                return true;
            }
        },
    });

    const rate = useConversionRate({from: accountFrom, to: accountTo});

    const amount = RsnAmount.toBigNumber(values.amount);
    const approxAmount = amount.multipliedBy(rate);
    const fee = amount.multipliedBy(relativeFee).dividedBy(100).plus(absoluteFee);

    /* NOTE: Current fee handling is designed for EUR purchases only.
    when implementing crypto purchases with other fiat currencies,
    the fee we need something to do with it, because we expect that
    fee is in the same currency as an account we are using to buy crypto */
    const withFeeSubtracted = amount.minus(fee);
    const totalReceive = withFeeSubtracted.isNegative() ? BigNumber(0) : withFeeSubtracted.multipliedBy(rate);

    const navigate = useNavigate();
    const closeDialog = () => navigate(-1);

    if (!isFormCompleted && direction.view !== 'form') {
        return <Navigate to={createOperationPath(accountFrom, accountTo)} replace />;
    }

    if (direction.view === 'finish') {
        return (
            //FIXME: imported from another process
            <OrderPlacedInfoPage
                goToDetails={() => executeOperationFinishNavigation(direction.orderId, navigate)}
                amount={totalReceive}
                currency={accountTo}
                direction={'in'}
                details={'Purchase request submitted.\nProcessing may take some time.'}
            />
        );
    }
    return (
        <RootLayout title={t.Title}>
            <form className={'flex h-full flex-col'} onSubmit={onSubmit}>
                <div className={'flex flex-1 flex-col gap-6 p-4'}>
                    <OperationSelectButton
                        label={'From'}
                        iconSrc={accountFrom.icon}
                        heading={accountFrom.name}
                        details={t.BalanceFrom}
                    />
                    <OperationSelectButton
                        label={'To'}
                        iconSrc={accountTo.icon}
                        heading={accountTo.name}
                        details={t.BalanceTo}
                    />
                </div>
                <div className={'flex flex-col gap-4 p-4'}>
                    <RsnAmountInput
                        {...fields.amount}
                        helperText={t.MinHint}
                        suffix={accountFrom.code}
                        maxIntegers={10}
                        maxDecimals={Math.min(accountFrom.decimals, MAX_DECIMALS_IN_CRYPTO)}
                        hintAction={<RsnAmountInput.HintActionUseAll all={accountFrom.balance} text={t.UseAllFunds} />}
                    />
                    <OperationDetail.Layout>
                        <OperationDetail label={t.ApproxValue}>{formatAsApprox(approxAmount)}</OperationDetail>
                        <OperationDetail label={t.Fee}>{formatAsSource(fee)}</OperationDetail>
                        <OperationDetail label={t.TotalReceive} variant={'total'}>
                            {formatAsTarget(totalReceive)}
                        </OperationDetail>
                    </OperationDetail.Layout>
                    <RsnButton.Primary type={'submit'} disabled={isSubmitDisabled}>
                        {t.Buy}
                    </RsnButton.Primary>
                </div>
            </form>

            <RsnDialog.Controller isOpen={direction.dialog === 'not-enough-funds'} onClose={closeDialog}>
                <NotEnoughFundsDialog />
            </RsnDialog.Controller>
        </RootLayout>
    );
}

function LoadingPage() {
    return (
        <RootLayout>
            <div className={'flex h-full flex-col'}>
                <div className={'flex flex-1 flex-col gap-6 p-4'}>
                    <OperationSelectButton.Skeleton />
                    <OperationSelectButton.Skeleton />
                </div>
                <div className={'flex flex-col gap-4 p-4'}>
                    <RsnAmountInput.Skeleton />
                    <div className={'flex items-center justify-between'}>
                        <RsnSkeleton width={70} height={16} />
                        <RsnSkeleton width={80} height={16} />
                    </div>
                    <div className={'flex items-center justify-between'}>
                        <RsnSkeleton width={20} height={16} />
                        <RsnSkeleton width={70} height={16} />
                    </div>
                    <div className={'flex items-center justify-between'}>
                        <RsnSkeleton width={70} height={16} />
                        <RsnSkeleton width={100} height={25} />
                    </div>
                    <RsnSkeleton height={48} borderRadius={16} />
                </div>
            </div>
        </RootLayout>
    );
}

function NotEnoughFundsDialog() {
    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader>Not enough funds</RsnDialog.CenteredHeader>
            <RsnDialog.Body>You have insufficient funds in your account to complete the purchase.</RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Alike intent='secondary' as={RsnDialog.CloseTrigger}>
                    I got it
                </RsnButton.Alike>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

function useErrorHandler() {
    const dispatch = useAppDispatch();
    return (error: unknown): never => {
        dispatch(GenericErrorMsg.open());
        throw error;
    };
}

const createOperationPath = (from: FiatAccount, to: BlockchainAccount) => {
    return `/operation/exchange/${from.type}/${from.id}/${to.type}/${to.id}`;
};
